import React, { useEffect, useMemo, useState } from 'react';
import { Handle, Position } from 'reactflow';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../ChannelsNode.module.scss';
import XMTP from '../../../../../assets/icons/flows/xmtp.svg';
import Edit from '../../../../../assets/icons/flows/edit.svg';
import Alert from '../../../../../assets/icons/flows/alert.svg';
import {
  clearActiveSettings,
  deleteSettings,
  selectConfigsList,
  selectErrors,
  selectFlowStatus,
  setActiveSettings,
  setXMTPKey,
} from '../../../../../store/reducers/flows';
import Delete from '../../../../../assets/icons/flows/delete.svg';
import { handle, handleInput } from '../../../../../utils/flows';
import Tooltip from '../../../../../components/ui/Tooltip';
import Divider from '../../../../../assets/icons/divider.svg';
import { userApi } from '../../../../../api/user';
import Spinner from '../../../../../components/base/Spinner';
import { showErrorMessage } from '../../../../../components/base/Notifications';

const XMTPNode = ({ ...props }) => {
  const dispatch = useDispatch();

  const [data, setData] = useState();
  const [completed, setCompleted] = useState(false);
  const [onNodeHover, setOnNodeHover] = useState(false);

  const configList = useSelector(selectConfigsList);
  const errors = useSelector(selectErrors);
  const status = useSelector(selectFlowStatus);

  const [isXMTPKeyExist, result] = userApi.useGetXMTPKeyMutation();

  const handleEdit = useMemo(() => {
    if (result.isLoading || result.isFetching) {
      return (
        <div className="d-flex align-item-center">
          <Spinner />
        </div>
      );
    }
    return (
      <div
        className="cursor-pointer"
        role="presentation"
        onClick={(e) => {
          e.preventDefault();
          isXMTPKeyExist();
        }}
      >
        <Edit />
      </div>
    );
  }, [result?.isFetching, result?.isLoading, isXMTPKeyExist]);

  const filteredErrors = () =>
    errors.length && errors.filter((elem) => elem.node_id === props.id);

  useEffect(() => {
    if (configList.length) {
      const index = configList
        .map((object) => object.node_id)
        .indexOf(props.id);
      if (index >= 0) {
        setData(configList[index]);
      }
    }
  }, [configList, props.id]);

  useEffect(() => {
    if (data && data.message) {
      setCompleted(true);
    } else {
      setCompleted(false);
    }
  }, [data]);

  useEffect(() => {
    if (result.isSuccess && result.data) {
      dispatch(setActiveSettings({ type: 'XMTPNode', node_id: props.id }));
      result.reset();
    }
    if (result.isSuccess && !result.data) {
      dispatch(setXMTPKey(true));
      result.reset();
    }
    if (result.isError) {
      showErrorMessage('Something went wrong');
      result.reset();
    }
  }, [dispatch, props?.id, result]);

  return (
    <>
      <div
        className={`
        ${styles.wrapper}
        ${data && data.message ? styles.size120 : ''}
        ${status === 'running' || status === 'scheduled' || status === 'stopped' ? 'cursor-pointer' : ''}
        d-flex
        flex-column
        justify-content-center
        align-items-start
        `}
        onMouseEnter={() => setOnNodeHover(true)}
        onMouseLeave={() => setOnNodeHover(false)}
        role="presentation"
        onClick={() => {
          if (
            status === 'running' ||
            status === 'scheduled' ||
            status === 'stopped'
          ) {
            dispatch(
              setActiveSettings({ type: 'XMTPNode', node_id: props.id })
            );
          }
        }}
      >
        <div
          className={`${styles.title} d-flex align-items-center justify-content-between gap-2 w-100`}
        >
          <div className="d-flex align-items-center gap-2">
            {status !== 'running' &&
            status !== 'stopped' &&
            status !== 'scheduled' ? (
              <>
                {!completed || filteredErrors().length ? (
                  <>
                    <div
                      className={styles.alert}
                      data-for={`${props.id}_error`}
                      data-tip
                    >
                      <Alert />
                    </div>
                    {filteredErrors().length ? (
                      <Tooltip
                        id={`${props.id}_error`}
                        info={`${filteredErrors()[0].detail}`}
                      />
                    ) : null}
                  </>
                ) : null}
                <div
                  className={onNodeHover ? styles.delete : styles.delete_hide}
                >
                  <Delete
                    onClick={() => {
                      props.data.onRemove(props.id);
                      dispatch(deleteSettings(props.id));
                      dispatch(clearActiveSettings());
                    }}
                  />
                </div>
              </>
            ) : null}
            <XMTP />
            <div className={styles.description}>
              {(data && data.name) || 'XMTP'}
            </div>
          </div>
          {handleEdit}
        </div>
        {data && data.message ? (
          <div>
            <div className={`${styles.divider} d-flex`}>
              <Divider />
            </div>
            <div className={`${styles.info} d-flex`}>
              <div className={`${styles.info_col} d-flex gap-1 flex-column`}>
                <div>Message:</div>
                <div>
                  ”
                  {data.message.length > 25
                    ? `${data.message.slice(0, 25)}...`
                    : data.message}
                  ”
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <Handle
        type="target"
        position={Position.Left}
        style={{ ...handleInput, left: '-10px' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ ...handle, right: '-16px' }}
        id="XMTPNode_output"
      />
    </>
  );
};

export default XMTPNode;
